import React from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Crown, Trophy, X, CheckCircle } from "lucide-react";

const InviteConfirmationModal = ({
	isOpen,
	onClose,
	inviteData,
	isLoading = false,
}) => {
	if (!inviteData) {
		return null;
	}

	const {
		partyName,
		contestName,
		role,
		partyIcon,
		partyIconType,
		memberCount,
		inviter,
		inviteType,
	} = inviteData;

	const getRoleIcon = (role) => {
		switch (role?.toLowerCase()) {
			case "gamemaster":
				return <Crown className="h-5 w-5 text-yellow-500" />;
			case "ringleader":
				return <Users className="h-5 w-5 text-blue-500" />;
			case "judge":
				return <Trophy className="h-5 w-5 text-purple-500" />;
			default:
				return <Users className="h-5 w-5 text-green-500" />;
		}
	};

	const getRoleDisplayName = (role) => {
		switch (role?.toLowerCase()) {
			case "gamemaster":
				return "Game Master";
			case "ringleader":
				return "Ringleader";
			case "judge":
				return "Judge";
			case "member":
				return "Member";
			default:
				return role || "Member";
		}
	};

	const getInviteTypeMessage = (type) => {
		switch (type) {
			case "gamemaster-recruited-member":
			case "gamemaster-recruited-ringleader":
			case "gamemaster-recruited-judge":
				return "You've been recruited by a Game Master";
			case "member-recruited":
				return "You've been invited by a party member";
			case "ringleader-recruited":
				return "You've been invited by a Ringleader";
			default:
				return "You've been invited to join";
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<DialogTitle className="text-xl font-semibold">
						Welcome to the Party!
					</DialogTitle>
					<Button
						variant="ghost"
						size="sm"
						onClick={onClose}
						className="h-8 w-8 p-0"
					>
						<X className="h-4 w-4" />
					</Button>
				</DialogHeader>

				<Card className="border-0 shadow-none">
					<CardContent className="space-y-4 p-0">
						{/* Success indicator */}
						<div className="mb-4 flex items-center justify-center">
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
								<CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
							</div>
						</div>

						{/* Main message */}
						<div className="space-y-2 text-center">
							<p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300">
								🎉 Congratulations! You've been added to the{" "}
								<span className="font-bold text-purple-600 dark:text-purple-400">
									{contestName || "Contest"} contest
								</span>
								{role && (
									<>
										{" "}
										as a{" "}
										<span className="font-bold text-blue-600 dark:text-blue-400">
											{getRoleDisplayName(role)}
										</span>
									</>
								)}
								{partyName && partyName !== "Party" && (
									<>
										{" "}
										for the{" "}
										<span className="font-bold text-orange-600 dark:text-orange-400">
											{partyName} party
										</span>
									</>
								)}
								!
							</p>
						</div>

						{/* Party details */}
						<div className="space-y-3">
							{/* Role */}
							<div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
								<div className="flex items-center space-x-2">
									{getRoleIcon(role)}
									<span className="text-sm font-medium">Your Role</span>
								</div>
								<span className="text-sm font-semibold">
									{getRoleDisplayName(role)}
								</span>
							</div>

							{/* Party icon */}
							{partyIcon && (
								<div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
									<span className="text-sm font-medium">Party Icon</span>
									<div className="text-2xl">
										{partyIconType === "emoji" ? (
											partyIcon
										) : (
											<img
												src={partyIcon}
												alt="Party Icon"
												className="h-6 w-6 object-contain"
											/>
										)}
									</div>
								</div>
							)}

							{/* Member count */}
							{memberCount !== undefined && (
								<div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
									<div className="flex items-center space-x-2">
										<Users className="h-4 w-4 text-muted-foreground" />
										<span className="text-sm font-medium">Party Size</span>
									</div>
									<span className="text-sm font-semibold">
										{memberCount} member{memberCount !== 1 ? "s" : ""}
									</span>
								</div>
							)}

							{/* Inviter */}
							{inviter && (
								<div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
									<span className="text-sm font-medium">Invited by</span>
									<span className="text-sm font-semibold">{inviter}</span>
								</div>
							)}
						</div>

						{/* Loading state */}
						{isLoading && (
							<div className="py-4 text-center">
								<div className="inline-flex items-center space-x-2">
									<div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
									<span className="text-sm text-muted-foreground">
										Processing invitation...
									</span>
								</div>
							</div>
						)}

						{/* Dismiss button */}
						<div className="pt-4">
							<Button onClick={onClose} className="w-full" disabled={isLoading}>
								{isLoading ? "Processing..." : "Got it!"}
							</Button>
						</div>
					</CardContent>
				</Card>
			</DialogContent>
		</Dialog>
	);
};

export default InviteConfirmationModal;
